import React, { useEffect, useState, useRef } from 'react';

interface IFrameComponentProps {
    htmlUrl: string;
    jsUrl: string;
    replacements: Array<[string, string]>;
    title?: string;
    extraScript?: string;
    extraCss?: string;
    isTransparent?: boolean;
}

const IFrameComponent: React.FC<IFrameComponentProps> = ({
    htmlUrl,
    jsUrl,
    replacements,
    title = 'widget-preview',
    extraScript = '',
    extraCss = '',
    isTransparent = false,
}) => {
    const [htmlContent, setHtmlContent] = useState('');
    const [scriptContent, setScriptContent] = useState('');
    // Unique stable ID per mounted instance to avoid collisions when multiple widgets exist
    const instanceId = useRef(`${title}-${Math.random().toString(36).slice(2)}`);

    useEffect(() => {
        if (!htmlUrl) return;
        const fetchContent = async () => {
            try {
                // Fetch HTML
                const response = await fetch(htmlUrl);
                let html = await response.text();

                // Fetch Script (optional – some widgets have no separate JS)
                let script = '';
                if (jsUrl) {
                    const scriptResponse = await fetch(jsUrl);
                    script = await scriptResponse.text();
                }

                replacements.forEach(([oldStr, newStr]) => {
                    // Use a function for replacement to avoid '$' being interpreted by String.prototype.replace
                    const safeReplace = (content: string, key: string, val: string, useWordBoundary: boolean) => {
                        const pattern = useWordBoundary ? `\\b${key}\\b` : key;
                        const regex = new RegExp(pattern, 'g');
                        return content.replace(regex, () => val);
                    };

                    html = safeReplace(html, oldStr, newStr, true);

                    // Only replace in script if it's NOT a complex object 
                    // to avoid mangling variable declarations like const scrollData = ...
                    // We use word boundaries for scripts to avoid mangling part of unrelated variables.
                    if (!newStr.startsWith('{') && !newStr.startsWith('[')) {
                        script = safeReplace(script, oldStr, newStr, true);
                    }
                });

                setHtmlContent(html);
                setScriptContent(script);
            } catch (error) {
                console.error('Error fetching widget content:', error);
            }
        };

        fetchContent();
    }, [htmlUrl, jsUrl, replacements]);

    useEffect(() => {
        if (htmlContent) {
            const iframe = document.getElementById(instanceId.current) as HTMLIFrameElement;
            if (!iframe) return;

            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!iframeDoc) return;

            // Derive base URL from htmlUrl so relative resources (images, css)
            // resolve against the widget's own server, not the Vite dev server.
            let baseUrl = '';
            try {
                const parsed = new URL(htmlUrl);
                baseUrl = `${parsed.origin}${parsed.pathname.substring(0, parsed.pathname.lastIndexOf('/') + 1)}`;
            } catch {
                // relative URL — skip
            }

            // Safety globals: widgets expect the CMS to pre-populate these before loading.
            // We use string literals for keys so they don't get caught in the replacements.
            const safetyScript = `
<script>
  (function() {
    function createSafe(name) {
      return new Proxy({}, {
        get: (target, prop) => {
          if (prop === 'includes') return () => false;
          if (prop === 'length') return 0;
          if (prop === 'toString') return () => "";
          if (prop === Symbol.iterator) return undefined;
          return createSafe(name + '.' + String(prop));
        }
      });
    }
    const globals = [
      'scroll' + 'Data', 
      'widget' + 'Data', 
      'rss' + 'Data', 
      'ticker' + 'Data', 
      'weather' + 'Data', 
      'forex' + 'Data', 
      'schedule' + 'Data', 
      'news' + 'Data', 
      'sports' + 'Data', 
      'stock' + 'Data'
    ];
    globals.forEach(g => {
      if (!window[g] || (Array.isArray(window[g]) && window[g].length === 0)) {
        window[g] = createSafe(g);
      }
    });
    window['loadWidget'] = window['loadWidget'] || (() => console.log('loadWidget stub called'));
  })();
<\/script>`;

            let dataInjectionScript = '<script>\n';
            replacements.forEach(([k, v]) => {
                let safeValue = v;
                try {
                    // If it's already a valid JSON string (like an object/array), use it as a literal
                    JSON.parse(v);
                } catch (e) {
                    // If it's a plain string, wrap it in quotes
                    safeValue = JSON.stringify(v);
                }
                // Escape </script> if it exists within the string values to avoid terminating the injection script early
                if (typeof safeValue === 'string') {
                    safeValue = safeValue.replace(/<\/script>/gi, '<\\/script>');
                }

                // Define as both window property and a global var for maximum compatibility
                dataInjectionScript += `  window['${k}'] = ${safeValue};\n`;
                if (/^[a-zA-Z_$][a-zA-Z_$0-9]*$/.test(k)) {
                    dataInjectionScript += `  var ${k} = ${safeValue};\n`;
                }
            });
            dataInjectionScript += '<\/script>';

            const baseTag = baseUrl ? `<base href="${baseUrl}" />` : '';

            // 1. Strip all <script> tags from fetched HTML to prevent double-loading 
            // the original/unreplaced scripts from the server.
            let cleanHtml = htmlContent.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

            // 2. Build the final delivery package
            let iframeContent = '';

            const transparencyScript = isTransparent
                ? '<style>body, html, .wejha-clock-container, .wejha-whole-container { background: transparent !important; background-color: transparent !important; }</style>'
                : '';

            // Search case-insensitively for <head> or <html>
            const headMatch = cleanHtml.match(/<head[^>]*>/i);
            if (headMatch) {
                const insertAt = headMatch.index! + headMatch[0].length;
                iframeContent = cleanHtml.slice(0, insertAt)
                    + "\n" + baseTag
                    + "\n" + transparencyScript
                    + "\n" + safetyScript
                    + "\n" + dataInjectionScript
                    + cleanHtml.slice(insertAt);
            } else {
                iframeContent = "<head>\n" + baseTag + "\n" + transparencyScript + "\n" + safetyScript + "\n" + dataInjectionScript + "\n</head>\n" + cleanHtml;
            }

            // Append styles and replaced scripts
            if (extraCss) {
                iframeContent += `\n<style>${extraCss}</style>`;
            }
            if (scriptContent) {
                // Ensure the script runs after the DOM is ready
                iframeContent += `\n<script>\n${scriptContent}\n<\/script>`;
            }
            if (extraScript) {
                iframeContent += `\n<script>\n${extraScript}\n<\/script>`;
            }

            iframeDoc.open();
            iframeDoc.write(iframeContent);
            iframeDoc.close();
        }
    }, [htmlContent, scriptContent, extraScript, extraCss, htmlUrl, replacements]);

    return (
        <iframe
            title={title}
            id={instanceId.current}
            style={{
                width: '100%',
                height: '100%',
                border: 'none',
                pointerEvents: 'none', // Keep none — glass pane in LayerRenderer handles editor events
                display: 'block',
                backgroundColor: isTransparent ? 'transparent' : 'white',
            }}
        />
    );
};

export default IFrameComponent;
